import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { cn } from "../lib/utils";
import {
  affichageDeLecture,
  ErreurColoriage,
  FilColoriage,
  IntrouvableColoriage,
  idValide,
  LIEN_BOUTON,
  LIEN_BOUTON_PLEIN,
  RelectureEchouee,
} from "./coloriage-commun";
import { ALERTE_GRISE, MessageDeRetour, type Retour } from "./coloriage-demandes";
import { BADGE_VERT, PointJugement } from "./coloriage-points";
import { messageDEchec, useCloreComparaison, useComparaison } from "./coloriage-requetes";
import {
  auteurEnClair,
  avancementDesJugements,
  avancementDuRendu,
  bilanDeComparaison,
  effacementDeComparaison,
  formeDeComparaison,
  JUGEMENTS,
  jugementEnClair,
  lectureDuTableau,
  NOM_REFERENCE,
  RENDU_AVANT_JUGEMENT,
  titreComparaison,
  titrePage,
} from "./coloriage-textes";
import type { Comparaison, LigneComparaison } from "./contract";
import { ConsoleShell } from "./shell";
import { Chargement } from "./states";

/**
 * Une comparaison (`/coloriage/comparaisons/$id`), ecran 5 du module. Textes et disposition :
 * `ColoriageComparer.dc.html`.
 *
 * L'ecran a deux visages, et c'est le backend qui tranche : **tant que `tableau` est null**
 * (il manque un jugement), la page ne montre que l'avancement et le bouton qui mene au mode a
 * l'aveugle, parce que lire le tableau pendant qu'on juge, c'est deja savoir laquelle est
 * laquelle. Une fois tout juge, le tableau sort avec les noms des variantes, la phrase de
 * lecture, la relance sur d'autres pages et la cloture.
 *
 * La page se relit toutes les 5 s tant qu'elle est ouverte et qu'il reste a juger
 * (`useComparaison`) : ses versions arrivent du PC pendant ce temps-la.
 */

/** Une case du tableau : une variante sur une page, ses trois comptes (le contrat n'en exporte
 * que le schema, pas le type). */
type Case = LigneComparaison["cases"][number];

/** Les trois segments d'une case, dans l'ordre des jugements. Tokens de `theme.css`, section 6. */
const SEGMENTS: readonly {
  readonly champ: "bonnes" | "defauts" | "a_jeter";
  readonly fond: string;
}[] = [
  { champ: "bonnes", fond: "bg-jugement-bonne" },
  { champ: "defauts", fond: "bg-jugement-defaut" },
  { champ: "a_jeter", fond: "bg-jugement-jeter" },
];

export function ColoriageComparaisonPage({ id }: { id: string }) {
  const valide = idValide(id);
  const lecture = useComparaison(id, valide);
  // Jamais l'ecran d'erreur et le tableau ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const comparaison = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilColoriage>
        <Link className="underline-offset-2 hover:underline" to="/coloriage/variantes">
          Variantes
        </Link>
        <span aria-hidden="true"> › </span>
        {comparaison === undefined ? "Comparaison" : titreComparaison(comparaison)}
      </FilColoriage>
      {!valide ? <IntrouvableColoriage quoi={`la comparaison ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="de la comparaison" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi={`la comparaison ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {comparaison === undefined ? null : <Tableau comparaison={comparaison} />}
    </ConsoleShell>
  );
}

function Tableau({ comparaison }: { comparaison: Comparaison }) {
  const [retour, setRetour] = useState<Retour | null>(null);
  // Deux conditions, pas une : le contrat promet `tableau` null tant qu'il manque un jugement,
  // mais un backend qui se tromperait ne doit pas reveler les noms pour autant.
  const tableau = comparaison.jugees >= comparaison.total ? comparaison.tableau : null;
  const sousTitre = [
    comparaison.cahier.titre,
    formeDeComparaison(
      comparaison.pages.length,
      comparaison.versions_par_page,
      comparaison.variantes_comptees,
    ),
  ].join(" · ");

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold">{titreComparaison(comparaison)}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{sousTitre}</p>
        </div>
        <span
          className={cn(
            "inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
            tableau === null ? "bg-muted text-muted-foreground" : BADGE_VERT,
          )}
        >
          {avancementDesJugements(comparaison)}
          {tableau === null ? ", variantes cachées" : ", noms révélés"}
        </span>
      </header>

      {tableau === null ? (
        <ACacher comparaison={comparaison} />
      ) : (
        <>
          <Grille comparaison={comparaison} tableau={tableau} />
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Ce que dit la comparaison</CardTitle>
            </CardHeader>
            <CardContent className="max-w-[80ch] text-sm leading-relaxed">
              {lectureDuTableau(tableau)}
            </CardContent>
          </Card>
          <Cloture annoncer={setRetour} comparaison={comparaison} retour={retour} />
        </>
      )}
    </>
  );
}

/**
 * Tant qu'il manque un jugement : l'avancement, et le bouton qui mene a l'aveugle. Ni nom de
 * variante, ni tableau : le backend ne les envoie pas, et la page n'a rien a en deviner.
 *
 * Le bouton n'apparait qu'une fois **tout rendu** (`arrivees === total`), et la phrase du rendu
 * dit pourquoi : l'agent rend une variante apres l'autre, donc juger pendant le rendu, c'est
 * juger une serie entiere issue d'une seule variante. La route `prochaine` refuse d'ailleurs de
 * servir quoi que ce soit avant, et l'ecran ne propose pas une porte que le backend tient fermee.
 */
function ACacher({ comparaison }: { comparaison: Comparaison }) {
  const part =
    comparaison.total === 0 ? 0 : Math.round((comparaison.jugees / comparaison.total) * 100);
  const reste = comparaison.total - comparaison.jugees;
  const rendu = comparaison.arrivees >= comparaison.total;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id="coloriage-comparaison-avancement">
          Juger à l'aveugle
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3.5">
        {rendu ? null : <p className="text-sm font-medium">{avancementDuRendu(comparaison)}</p>}
        <p className="text-sm">
          {avancementDesJugements(comparaison)}
          {reste > 0 ? `, il en reste ${reste}.` : "."}
        </p>
        <div
          aria-labelledby="coloriage-comparaison-avancement"
          aria-valuemax={comparaison.total}
          aria-valuemin={0}
          aria-valuenow={comparaison.jugees}
          aria-valuetext={avancementDesJugements(comparaison)}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
          role="progressbar"
        >
          <div className="h-full bg-foreground" style={{ width: `${part}%` }} />
        </div>
        <p className={ALERTE_GRISE}>
          {rendu ? (
            <>
              Les versions arrivent mélangées, et rien ne dit de quelle variante elles viennent : ni
              prompt, ni graine, ni nom. Le tableau et les noms reviennent quand les{" "}
              {comparaison.total} sont jugées.
            </>
          ) : (
            RENDU_AVANT_JUGEMENT
          )}
        </p>
        {rendu && reste > 0 ? (
          <div>
            <Link
              className={LIEN_BOUTON_PLEIN}
              params={{ id: String(comparaison.id) }}
              to="/coloriage/comparaisons/$id/juger"
            >
              Juger à l'aveugle
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Le tableau variantes x pages : trois compteurs par case, et le total par variante. */
function Grille({
  comparaison,
  tableau,
}: {
  comparaison: Comparaison;
  tableau: readonly LigneComparaison[];
}) {
  const entete = "px-3 pb-2 font-semibold";
  const titreId = "coloriage-comparaison-tableau";
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base" id={titreId}>
          Bonnes · défauts · à jeter, sur {comparaison.versions_par_page} versions par page
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3.5">
        {/* Une colonne par page : sur telephone la table defile a l'horizontale. */}
        <div className="overflow-x-auto">
          <table aria-labelledby={titreId} className="w-full min-w-[42rem] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-muted-foreground">
                <th className={cn(entete, "w-[12rem]")} scope="col">
                  Variante
                </th>
                {comparaison.pages.map((page) => (
                  <th className={entete} key={page.id} scope="col">
                    {titrePage(page.rang, page.titre)}
                  </th>
                ))}
                <th className={entete} scope="col">
                  Bonnes
                </th>
              </tr>
            </thead>
            <tbody>
              {tableau.map((ligne) => (
                <LigneDuTableau
                  key={ligne.variante === null ? "reference" : ligne.variante.id}
                  ligne={ligne}
                  pages={comparaison.pages}
                />
              ))}
            </tbody>
          </table>
        </div>
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {JUGEMENTS.map((jugement) => (
            <span className="flex items-center gap-1.5" key={jugement}>
              <PointJugement jugement={jugement} />
              {jugementEnClair(jugement)}
            </span>
          ))}
        </p>
      </CardContent>
    </Card>
  );
}

function LigneDuTableau({
  ligne,
  pages,
}: {
  ligne: LigneComparaison;
  pages: Comparaison["pages"];
}) {
  const cellule = "border-t border-border px-3 py-2.5 align-top";
  return (
    <tr>
      <td className={cellule}>
        <strong className="font-semibold">{ligne.variante?.nom ?? NOM_REFERENCE}</strong>
        <span className="block text-xs text-muted-foreground">{auteurEnClair(ligne.auteur)}</span>
      </td>
      {pages.map((page) => {
        const trois = ligne.cases.find((une) => une.page === page.id);
        return (
          <td className={cellule} key={page.id}>
            {trois === undefined ? (
              <span className="text-xs text-muted-foreground">rien</span>
            ) : (
              <CaseDuTableau
                nom={`${ligne.variante?.nom ?? NOM_REFERENCE}, ${titrePage(page.rang, page.titre)}`}
                trois={trois}
              />
            )}
          </td>
        );
      })}
      <td className={cn(cellule, "tabular-nums")}>
        <strong className="font-semibold">
          {ligne.bonnes} sur {ligne.total}
        </strong>
      </td>
    </tr>
  );
}

/**
 * Une case : la barre des trois jugements, puis leurs comptes. La barre est decorative
 * (`aria-hidden`), les comptes disent la meme chose en clair, avec un titre qui les nomme :
 * rien ne repose sur la couleur seule.
 */
function CaseDuTableau({ trois, nom }: { trois: Case; nom: string }) {
  const total = trois.bonnes + trois.defauts + trois.a_jeter;
  return (
    <>
      <span aria-hidden="true" className="flex h-2.5 overflow-hidden rounded-full bg-muted">
        {SEGMENTS.map((segment) => (
          <span
            className={cn("block h-full", segment.fond)}
            key={segment.champ}
            style={{ width: total === 0 ? "0%" : `${(trois[segment.champ] / total) * 100}%` }}
          />
        ))}
      </span>
      <span
        className="mt-1 block text-xs tabular-nums text-muted-foreground"
        title={`${nom} : ${trois.bonnes} bonnes, ${trois.defauts} défauts, ${trois.a_jeter} à jeter`}
      >
        {trois.bonnes} · {trois.defauts} · {trois.a_jeter}
      </span>
    </>
  );
}

/**
 * Ce qu'on fait d'une comparaison jugee : la relancer sur d'autres pages (le bloc de lancement
 * de l'ecran des variantes se pre-remplit), ou la clore, ce qui efface les images de ses
 * versions et garde leurs jugements, qui font le taux de chaque variante.
 */
function Cloture({
  comparaison,
  retour,
  annoncer,
}: {
  comparaison: Comparaison;
  retour: Retour | null;
  annoncer: (retour: Retour | null) => void;
}) {
  const clore = useCloreComparaison(String(comparaison.id));
  // Un verrou synchrone : un double clic arrive avant que `isPending` ne soit rendu.
  const envoi = useRef(false);
  const close = comparaison.etat === "close";

  const confirmer = (): void => {
    if (envoi.current) return;
    envoi.current = true;
    annoncer(null);
    clore.mutate(undefined, {
      onSuccess: (bilan) => annoncer({ ton: "ok", texte: bilanDeComparaison(bilan) }),
      onError: (error) => annoncer({ ton: "echec", texte: messageDEchec(error) }),
      onSettled: () => {
        envoi.current = false;
      },
    });
  };

  return (
    <section
      aria-label="Suites de la comparaison"
      className="flex flex-col gap-3 rounded-lg border border-border bg-card px-5 py-3.5 text-card-foreground"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2.5">
        <span className="flex-1 text-sm text-muted-foreground">
          {close
            ? "Comparaison close : les images de ses versions sont effacées, leurs jugements font le taux de chaque variante."
            : effacementDeComparaison(comparaison)}
        </span>
        <Link className={LIEN_BOUTON} search={{ depuis: comparaison.id }} to="/coloriage/variantes">
          Relancer sur d'autres pages
        </Link>
        {close ? null : (
          <Button
            aria-disabled={clore.isPending || undefined}
            className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={confirmer}
          >
            Clore la comparaison
          </Button>
        )}
      </div>
      <MessageDeRetour retour={retour} />
    </section>
  );
}
